import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getRevenueSchedules, getRevenueSchedule, getRevenueReport, getDeferredBalance, postRevenueEntry,
} from '../services/api';
import { formatCurrency, formatDate, StatusBadge } from '../utils/helpers';
import { TrendingUp, BarChart2, BookOpen, ChevronRight, ChevronDown, CheckCircle2, ExternalLink } from 'lucide-react';
import toast from 'react-hot-toast';

function ScheduleEntriesPanel({ scheduleId, onPosted }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [posting, setPosting] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    getRevenueSchedule(scheduleId)
      .then(r => setDetail(r.data?.schedule || null))
      .catch(() => setDetail(null))
      .finally(() => setLoading(false));
  }, [scheduleId]);

  useEffect(() => { load(); }, [load]);

  const handlePost = async (entry) => {
    setPosting(entry.period_month);
    try {
      await postRevenueEntry(scheduleId, { period_month: entry.period_month });
      toast.success(`Posted ${formatCurrency(entry.recognised_amount)} to O2C/Tally`);
      load();
      onPosted?.();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to post');
    } finally {
      setPosting(null);
    }
  };

  if (loading) return <div className="px-5 py-3 text-xs text-gray-400">Loading periods…</div>;
  if (!detail?.entries?.length) return <div className="px-5 py-3 text-xs text-gray-400">No entries</div>;

  const currency = detail.currency || 'INR';

  return (
    <div className="bg-violet-50/30 border-t border-violet-100 px-5 py-3">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-xs text-gray-500">
            {['Period', 'Normal', 'Catch-up', 'Posted amount', 'Status', 'Action'].map(h => (
              <th key={h} className="text-left font-medium py-1 pr-2">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {detail.entries.map(entry => {
            const catchup = parseFloat(entry.catchup_adjustment_amount || 0);
            const net = parseFloat(entry.recognised_amount || 0);
            const normal = catchup > 0.009 ? Math.round((net + catchup) * 100) / 100 : net;
            return (
              <tr key={entry.entry_id} className={entry.status === 'POSTED' ? 'text-green-700' : ''}>
                <td className="py-1.5 pr-2 font-medium">{entry.period_label}</td>
                <td className="py-1.5 pr-2">{formatCurrency(normal, currency)}</td>
                <td className="py-1.5 pr-2 text-violet-700">
                  {catchup > 0.009 ? `−${formatCurrency(catchup, currency)}` : '—'}
                </td>
                <td className="py-1.5 pr-2 font-semibold">{formatCurrency(net, currency)}</td>
                <td className="py-1.5 pr-2">
                  {entry.status === 'POSTED' ? (
                    <span className="text-xs text-green-600 flex items-center gap-1">
                      <CheckCircle2 size={11}/> Posted
                    </span>
                  ) : (
                    <StatusBadge status={entry.status} />
                  )}
                </td>
                <td className="py-1.5">
                  {entry.status === 'PENDING' && (
                    <button
                      type="button"
                      onClick={() => handlePost(entry)}
                      disabled={posting === entry.period_month}
                      className="text-xs px-2 py-0.5 rounded bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50"
                    >
                      {posting === entry.period_month ? '…' : 'Post'}
                    </button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default function Revenue() {
  const [schedules, setSchedules]   = useState([]);
  const [report, setReport]         = useState(null);
  const [deferred, setDeferred]     = useState([]);
  const [period, setPeriod]         = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}`;
  });
  const [loading, setLoading]       = useState(true);
  const [activeTab, setActiveTab]   = useState('schedules');
  const [expandedId, setExpandedId] = useState(null);
  const navigate = useNavigate();

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([getRevenueSchedules(), getDeferredBalance()])
      .then(([s, d]) => {
        setSchedules(s.data.schedules || []);
        setDeferred(d.data.deferred_balance || []);
      })
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (activeTab === 'report' && period.length === 6) {
      getRevenueReport(period)
        .then(r => setReport(r.data.report))
        .catch(() => setReport(null));
    }
  }, [period, activeTab]);

  const totalDeferred    = deferred.reduce((s, r) => s + parseFloat(r.deferred_balance || 0), 0);
  const totalRecognised  = deferred.reduce((s, r) => s + parseFloat(r.recognised_to_date || 0), 0);
  const activeSchedules  = schedules.filter(s => ['ACTIVE','PARTIALLY_RECOGNISED'].includes(s.status));

  const openSchedule = (s) => {
    if (s.invoice_id) navigate(`/invoices/${s.invoice_id}`);
    else if (s.po_id) navigate(`/purchase-orders/${s.po_id}`);
  };

  const tabs = [
    { id: 'schedules', label: 'Revenue Schedules',  icon: BookOpen },
    { id: 'report',    label: 'Period Report',       icon: BarChart2 },
    { id: 'deferred',  label: 'Deferred Balance',    icon: TrendingUp },
  ];

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-xl font-semibold">Revenue Recognition</h2>
        <p className="text-sm text-gray-500 mt-0.5">
          Invoice-linked schedules are set up from an{' '}
          <button type="button" onClick={() => navigate('/invoices')} className="text-primary-600 hover:underline">
            Invoice
          </button>
          {' '}detail page. After SO modification, use the <strong>SO schedule</strong> row (no invoice) — expand and Post each period, or post from the{' '}
          <button type="button" onClick={() => navigate('/purchase-orders')} className="text-primary-600 hover:underline">
            Sales Order
          </button>
          {' '}detail page.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="card p-5">
          <div className="text-xs text-gray-500 mb-1">Deferred Revenue Balance</div>
          <div className="text-2xl font-bold text-amber-600">{formatCurrency(totalDeferred)}</div>
          <div className="text-xs text-gray-400 mt-1">Invoiced, not yet recognised</div>
        </div>
        <div className="card p-5">
          <div className="text-xs text-gray-500 mb-1">Recognised to Date</div>
          <div className="text-2xl font-bold text-green-600">{formatCurrency(totalRecognised)}</div>
          <div className="text-xs text-gray-400 mt-1">Posted to P&L</div>
        </div>
        <div className="card p-5">
          <div className="text-xs text-gray-500 mb-1">Active Schedules</div>
          <div className="text-2xl font-bold text-primary-600">{activeSchedules.length}</div>
          <div className="text-xs text-gray-400 mt-1">In progress</div>
        </div>
      </div>

      <div className="flex gap-1 bg-gray-100 p-1 rounded-xl w-fit">
        {tabs.map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-all
              ${activeTab === t.id ? 'bg-white text-primary-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
            <t.icon size={14}/>{t.label}
          </button>
        ))}
      </div>

      {activeTab === 'schedules' && (
        <div className="card overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-gray-400">Loading…</div>
          ) : schedules.length === 0 ? (
            <div className="p-12 text-center">
              <TrendingUp size={36} className="mx-auto text-gray-300 mb-3"/>
              <p className="font-medium text-gray-500">No revenue schedules yet</p>
              <p className="text-sm text-gray-400 mt-1 max-w-md mx-auto">
                Per-invoice schedules: open an invoice and click <strong>Set Up Revenue Recognition</strong>.
                Post-mod SO schedules appear here automatically after SO modification.
              </p>
            </div>
          ) : (
            <table className="w-full" style={{tableLayout:'fixed'}}>
              <colgroup>
                <col style={{width:28}}/>
                <col style={{width:130}}/>
                <col/>
                <col style={{width:180}}/>
                <col style={{width:110}}/>
                <col style={{width:110}}/>
                <col style={{width:110}}/>
                <col style={{width:55}}/>
                <col style={{width:90}}/>
                <col style={{width:36}}/>
              </colgroup>
              <thead>
                <tr>
                  {['', 'Source', 'Customer / SO', 'Service Period', 'Total', 'Recognised', 'Deferred', 'Conv.', 'Status', ''].map(h => (
                    <th key={h || 'exp'} className="table-th">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {schedules.map(s => {
                  const recognised  = parseFloat(s.recognised_to_date || 0);
                  const deferred_   = parseFloat(s.deferred_balance || 0);
                  const total       = parseFloat(s.total_amount || 0);
                  const pct         = total > 0 ? Math.round(recognised / total * 100) : 0;
                  const isPoSchedule = !s.invoice_id && s.po_id;
                  const isExpanded = expandedId === s.schedule_id;
                  return (
                    <React.Fragment key={s.schedule_id}>
                      <tr className="hover:bg-gray-50">
                        <td className="table-td">
                          <button
                            type="button"
                            onClick={() => setExpandedId(isExpanded ? null : s.schedule_id)}
                            className="p-1 text-gray-400 hover:text-gray-700"
                            title="Expand periods"
                          >
                            <ChevronDown size={14} className={`transition-transform ${isExpanded ? 'rotate-180' : ''}`}/>
                          </button>
                        </td>
                        <td className="table-td text-xs">
                          {s.invoice_number ? (
                            <button type="button" onClick={() => openSchedule(s)}
                              className="font-mono text-primary-600 hover:underline">
                              {s.invoice_number}
                            </button>
                          ) : (
                            <span className="px-1.5 py-0.5 rounded bg-violet-100 text-violet-800 font-medium">
                              SO schedule
                            </span>
                          )}
                        </td>
                        <td className="table-td text-sm">
                          <div className="font-medium">{s.customer_name || '—'}</div>
                          <button type="button" onClick={() => s.po_id && navigate(`/purchase-orders/${s.po_id}`)}
                            className="text-xs text-primary-600 hover:underline">
                            {s.po_number || '—'}
                          </button>
                        </td>
                        <td className="table-td text-sm text-gray-600">
                          {formatDate(s.service_period_from)} → {formatDate(s.service_period_to)}
                          {s.apportionment_method === 'EQUAL_MONTHLY' && (
                            <span className="ml-1 text-[10px] text-violet-600">(catch-up)</span>
                          )}
                        </td>
                        <td className="table-td font-semibold">{formatCurrency(total)}</td>
                        <td className="table-td text-green-600 font-medium">{formatCurrency(recognised)}</td>
                        <td className="table-td text-amber-600 font-medium">{formatCurrency(deferred_)}</td>
                        <td className="table-td text-xs">
                          <div className="flex items-center gap-1.5">
                            <div className="w-16 bg-gray-100 rounded-full h-1.5">
                              <div className="bg-green-500 h-1.5 rounded-full" style={{ width: `${pct}%` }}/>
                            </div>
                            <span className="text-gray-400">{pct}%</span>
                          </div>
                        </td>
                        <td className="table-td"><StatusBadge status={s.status}/></td>
                        <td className="table-td">
                          <button type="button" onClick={() => openSchedule(s)} title={isPoSchedule ? 'Open SO' : 'Open invoice'}>
                            <ExternalLink size={13} className="text-gray-400 hover:text-primary-600"/>
                          </button>
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr>
                          <td colSpan={10} className="p-0">
                            <ScheduleEntriesPanel scheduleId={s.schedule_id} onPosted={load}/>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}

      {activeTab === 'report' && (
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <label className="label mb-0 whitespace-nowrap">Period (YYYYMM)</label>
            <input className="input max-w-xs" value={period}
              onChange={e => setPeriod(e.target.value)} placeholder="e.g. 202604"/>
          </div>

          {report ? (
            <>
              <div className="grid grid-cols-3 gap-4">
                {[
                  { label: 'Total Due This Period', value: report.totalRecognised, color: 'text-gray-900' },
                  { label: 'Posted to P&L',          value: report.posted,          color: 'text-green-600' },
                  { label: 'Pending',                value: report.pending,          color: 'text-amber-600' },
                ].map(k => (
                  <div key={k.label} className="card p-4 text-center">
                    <div className="text-xs text-gray-500 mb-1">{k.label}</div>
                    <div className={`text-xl font-bold ${k.color}`}>{formatCurrency(k.value)}</div>
                  </div>
                ))}
              </div>

              <div className="card overflow-x-auto">
                <table className="w-full" style={{tableLayout:'fixed'}}>
                  <colgroup>
                    <col style={{width:100}}/>
                    <col style={{width:130}}/>
                    <col style={{width:55}}/>
                    <col style={{width:90}}/>
                    <col style={{width:120}}/>
                    <col style={{width:140}}/>
                    <col style={{width:90}}/>
                  </colgroup>
                  <thead>
                    <tr>{['Period', 'SO', 'Days', 'Proration %', 'Amount', 'Accounting Period', 'Status'].map(h =>
                      <th key={h} className="table-th">{h}</th>
                    )}</tr>
                  </thead>
                  <tbody>
                    {report.entries?.length === 0 ? (
                      <tr><td colSpan={7} className="table-td text-center text-gray-400">No entries for this period</td></tr>
                    ) : report.entries?.map(e => (
                      <tr key={e.entry_id} className={`hover:bg-gray-50 ${e.status === 'POSTED' ? 'bg-green-50/30' : ''}`}>
                        <td className="table-td font-medium">{e.period_label}</td>
                        <td className="table-td text-xs text-gray-500">{e.po_number || e.po_id?.slice(0,8)}</td>
                        <td className="table-td">{e.days_in_period}</td>
                        <td className="table-td text-gray-500">{(parseFloat(e.proration_factor)*100).toFixed(2)}%</td>
                        <td className="table-td font-semibold">{formatCurrency(e.recognised_amount)}</td>
                        <td className="table-td text-xs font-mono text-gray-500">{e.accounting_period}</td>
                        <td className="table-td"><StatusBadge status={e.status}/></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            <div className="card p-8 text-center text-gray-400">
              Enter a period (e.g. <strong>202604</strong>) to load the report
            </div>
          )}
        </div>
      )}

      {activeTab === 'deferred' && (
        <div className="card overflow-x-auto">
          {deferred.length === 0 ? (
            <div className="p-8 text-center text-gray-400">No deferred revenue balance</div>
          ) : (
            <table className="w-full" style={{tableLayout:'fixed'}}>
              <colgroup>
                <col style={{width:130}}/>
                <col/>
                <col style={{width:130}}/>
                <col style={{width:150}}/>
                <col style={{width:140}}/>
              </colgroup>
              <thead>
                <tr>{['SO','Customer','Schedule Total','Recognised to Date','Deferred Balance'].map(h =>
                  <th key={h} className="table-th">{h}</th>
                )}</tr>
              </thead>
              <tbody>
                {deferred.map((d, i) => (
                  <tr key={i} className="hover:bg-gray-50">
                    <td className="table-td text-xs font-mono text-gray-500">{d.po_number || d.po_id?.slice(0,8)}</td>
                    <td className="table-td text-sm">{d.customer_name || '—'}</td>
                    <td className="table-td font-semibold">{formatCurrency(d.total_schedule_amount)}</td>
                    <td className="table-td text-green-600 font-medium">{formatCurrency(d.recognised_to_date)}</td>
                    <td className="table-td font-bold text-amber-600">{formatCurrency(d.deferred_balance)}</td>
                  </tr>
                ))}
                <tr className="bg-gray-50 font-semibold border-t-2">
                  <td className="table-td" colSpan={3}>Total</td>
                  <td className="table-td text-green-600">{formatCurrency(totalRecognised)}</td>
                  <td className="table-td text-amber-600">{formatCurrency(totalDeferred)}</td>
                </tr>
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}
